import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CmdAccordion } from '@commander-services/gui-components';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { debounce } from 'lodash';
import { useLocation, useRoute } from 'wouter';
import { AxiosResponse } from 'axios';
import ModalForm from './ModalForm';
import Header from './Header';
import Body from './Body';
import PermissionsService, {
  PERMISSION_VEHICLE_GROUP_CUSTOMER,
  PERMISSION_VEHICLE_GROUP_PRIVATE,
} from '../../Services/PermissionsService';
import './styles.scss';
import ConfirmDialog from '../ConfirmDialog';
import { showMessage, hideMessage } from '../Toastr/ToastService';
import Actions from './actions';
import { URL_VEHICLES_GROUPS } from '../../router/constants';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import OnlineHelp from '../OnlineHelp';
import { setLoaderAtom } from '../../store/recoil/AppState';
import { customersAtom, ICustomer, selectedCustomersAtom } from '../../store/recoil/customers';
import { IVehicleGroup } from '../Vehicles/types';
import { vehicleGroupsAtom } from '../../store/recoil/vehicles';
import { getRequest, putRequest, RESPONSE_STATUS_OK } from '../../Services/HttpService';
import useAnalytics from '../../hooks/useAnalytics';
import PageHeader from '../PageHeader';

interface ICarGroup {
  createdAt: string;
  createdBy: number;
  customerId: number;
  deletedAt: string | null;
  deletedBy: string | null;
  id: number;
  modifiedAt: string | null;
  modifiedBy: string | null;
  name: string;
  type: string;
  vehicles: {
    total: number;
    vehicleCustomerIds: number[];
  };
}

export interface IVehicleTypeName {
  [vehicleTypeId: number]: string;
}

// export const vehicleTypeName = {
//   1: 'car',
//   2: 'van',
//   3: 'truck',
//   4: 'person',
//   5: 'loader',
//   6: 'tractor',
//   7: 'motorcycle',
//   8: 'trailer',
//   9: 'train',
//   10: 'helicopter',
//   11: 'boat',
//   12: 'bicycle',
//   13: 'plane',
//   15: 'clearing_vehicle',
//   16: 'bus',
//   17: 'excavator',
//   18: 'dozer',
//   19: 'roller',
//   21: 'truck3',
//   22: 'truck4',
//   23: 'semi_truck',
//   30: 'semi_trailer',
//   32: 'pickup',
//   33: 'lorry',
//   36: 'ambulance',
//   38: 'power_generator',
//   39: 'tractor_advanced',
//   40: 'wood_export',
//   41: 'harvester',
// };

function CarsGroup() {
  const intl = useIntl();
  const { formatMessage: f } = useIntl();
  const [groupsToggle, setGroupsToggle] = React.useState<any>({});
  const [customerIdsInGroup, setCustomerIdsInGroup] = React.useState<{
    groupId: string;
    customerIds: number[];
  } | null>(null);
  const [editingValues, setEditingValues] = React.useState<any>({});
  const [allGroupsString, setAllGroupsString] = React.useState<string>('');
  const [isFormOpened, setIsFormOpened] = React.useState<boolean>(false);
  const [groups, setgroups] = React.useState<any>([]);
  const customers = useRecoilValue<ICustomer[]>(customersAtom);
  const [isRemoveButtonClicked, setIsRemoveButtonClicked] = React.useState<boolean>(false);
  const [isRemoveButtonConfirm, setIsRemoveButtonConfirm] = React.useState<boolean>(false);
  const [idForRemove, setIdForRemove] = React.useState<string | null>(null);
  const [groupNameToRemove, setGroupNameToRemove] = React.useState<string | null>(null);
  const setLoader = useSetRecoilState<boolean>(setLoaderAtom);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const setVehicleGroups = useSetRecoilState<IVehicleGroup[]>(vehicleGroupsAtom);
  const [pathname, navigate] = useLocation();
  const [, params] = useRoute(`${URL_VEHICLES_GROUPS}/edit/:id`);
  const { setDocumentTitle } = useDocumentTitle();
  const { trackEvent } = useAnalytics();

  const handleClose = () => {
    navigate(`${URL_VEHICLES_GROUPS}`);
    setIsFormOpened(false);
    trackEvent(['cars groups form', 'click', 'Close form']);
  };

  const loader = (value: boolean) => {
    setLoader(value);
  };

  // const setVehiclesGroupsInNavigator = (vehicleGroups: IVehicleGroup[]) => {
  //   setVehicleGroups(vehicleGroups);
  // };

  const getFilteredVehiclesGroups = async (searchString: string) => {
    const link = searchString ? `/v2/vehicle-group/list/${searchString}` : '/v2/vehicle-group/list';
    const response: AxiosResponse | false  = await getRequest(link, { returnAxiosResponse: true });
    loader(false);
    if (response && response.data && response.data.vehicleGroups) {
      setgroups(response.data.vehicleGroups);
      setVehicleGroups(response.data.vehicleGroups);
      const vehicleGroups: ICarGroup[] = response.data.vehicleGroups;
      if (
        Object.values(vehicleGroups).filter((item: ICarGroup) =>
          selectedCustomers.includes(item.customerId)
        ).length === 0
      ) {
        showMessage(f({ id: 'toastr.warning' }), f({ id: 'carsGroups.noData' }), 'warning');
      } else {
        hideMessage();
      }
    } else {
      setgroups([]);
    }

    let selectedGroup = '';
    if (groupsToggle && Object.keys(groupsToggle).length > 0) {
      selectedGroup = Object.keys(groupsToggle)[0];
    }
    if (
      !selectedGroup ||
      !groupsToggle[selectedGroup] ||
      !groups.find((i) => String(i.id) === selectedGroup)
    ) {
      setGroupsToggle({});
    }
  };

  const setGroups = (data: any) => {
    setgroups(data);
  };

  const removeGroup = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['cars groups form', 'click', 'Remove group']);

    const id = event.currentTarget.getAttribute('data-id');

    const groupName = groups.find((item) => String(item.id) === id);

    if (groupName && groupName.name) {
      setGroupNameToRemove(groupName.name);
    }

    setIsRemoveButtonClicked(true);
    setIdForRemove(id);
  };

  const toggleDetail = (id: string) => {
    trackEvent(['cars groups form', 'click', 'Toggle detail']);
    if (id) {
      if (
        (groupsToggle[id] !== undefined && groupsToggle[id] === false) ||
        groupsToggle[id] === undefined
      ) {
        setGroupsToggle({ [id]: true });
      } else if (groupsToggle[id] !== undefined && groupsToggle[id] === true) {
        setGroupsToggle({ [id]: false });
      }
    }
  };

  const editGroup = (item: any) => {
    setEditingValues(item);
    trackEvent(['cars groups form', 'click', 'Edit group']);
  };

  const handleEditGroupModal = (id: string) => {
    const item = groups.filter((i: IVehicleGroup) => String(i.id) === String(id))[0];
    editGroup(item);
    setIsFormOpened(true);
    navigate(`${URL_VEHICLES_GROUPS}/edit/${id}`);
    trackEvent(['cars groups form', 'click', 'Edit group']);
  };

  React.useEffect(() => {
    if (pathname === `${URL_VEHICLES_GROUPS}`) {
      const documentTitle = `${f({ id: 'carsGroups.carsGroups' })} - ${f({ id: 'menu.settings' })}`;
      setDocumentTitle(documentTitle);
    } else if (pathname === `${URL_VEHICLES_GROUPS}/add`) {
      setIsFormOpened(true);
      const documentTitle = `${f({ id: 'carsGroups.addGroup' })} - ${f({
        id: 'carsGroups.carsGroups',
      })} - ${f({ id: 'menu.settings' })}`;
      setDocumentTitle(documentTitle);
    } else if (groups.length && params && params.id) {
      handleEditGroupModal(params.id);
      const documentTitle = `${f({ id: 'carsGroups.editGroup' })} - ${f({
        id: 'carsGroups.carsGroups',
      })} - ${f({ id: 'menu.settings' })}`;
      setDocumentTitle(documentTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, groups]);

  const handleEditGroupModalOpening = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['cars groups form', 'click', 'Open edit group']);

    const id = event.currentTarget.getAttribute('item-id');
    if (id) {
      handleEditGroupModal(id);
    }
  };

  const debouncedAllGroupsSearch = debounce((value: string) => {
    setAllGroupsString(value);
    getFilteredVehiclesGroups(value);
  }, 300);

  const handleDebouncedAllGroupsSearch = (event: any) => {
    debouncedAllGroupsSearch(event.currentTarget.value);
  };

  const handleAddGroupModalOpening = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['cars groups form', 'click', 'Open add group modal']);

    setIsFormOpened(true);
    navigate(`${URL_VEHICLES_GROUPS}/add`);
    editGroup({});
  };

  React.useMemo(() => {
    if (!groups) {
      setGroups([]);
    }
  }, [groups]);

  React.useEffect(() => {
    loader(true);
    getFilteredVehiclesGroups('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomers]);

  const handleCancelRemove = () => {
    setIsRemoveButtonClicked(false);
    trackEvent(['cars groups form', 'click', 'Cancel remove']);
  };

  const deleteVehicleGroup = async () => {
    const response: AxiosResponse | false = await putRequest(
      `/v2/vehicle-group/${idForRemove}/remove`,
      {},
      { returnAxiosResponse: true }
    );
    if (response && response.data && response.data.status === RESPONSE_STATUS_OK) {
      const newGroups: any = [];
      groups.forEach((item: any) => {
        if (String(item.id) !== String(idForRemove)) {
          newGroups.push(item);
        }
      });
      setgroups(newGroups);
      setVehicleGroups(newGroups);
    }
    loader(false);
  };

  const handleContinue = async () => {
    setIsRemoveButtonConfirm(true);
    setIsRemoveButtonClicked(false);

    if (isRemoveButtonClicked) {
      loader(true);
      const response: AxiosResponse | false = await getRequest(
        `/v2/vehicle-group/${idForRemove}/verify/remove`,
        { returnAxiosResponse: true }
      );
      if (response && response.data && response.data.status === RESPONSE_STATUS_OK) {
        if (response.data.details && response.data.details.length > 0) {
          showMessage(
            f({ id: `toastr.warning` }),
            f({ id: response.data.details[0].message }),
            'warning'
          );
          loader(false);
        } else {
          deleteVehicleGroup();
        }
      }
    }
  };

  const handleCustomerIds = (data: { groupId: string; customerIds: number[] }) => {
    setCustomerIdsInGroup(data);
  };

  return (
    <div>
      <PageHeader
        title={`${f({ id: 'carsGroups.carsGroups' })} / ${f({ id: 'tab.vehicle' })}`}
        onlineHelpTopic="car-groups"
      />
      <div className="col-12">
        <div className="mt-4">
          <div className="row row--size-sm">
            <div className="col-12 col-md-6">
              {(PermissionsService.hasAccess(
                PERMISSION_VEHICLE_GROUP_PRIVATE,
                ['create'],
                selectedCustomers
              ) ||
                PermissionsService.hasAccess(
                  PERMISSION_VEHICLE_GROUP_CUSTOMER,
                  ['create'],
                  selectedCustomers
                )) && (
                <a
                  href="/"
                  onClick={handleAddGroupModalOpening}
                  title=""
                  className="e-button e-button--gray"
                  data-cy="cars-group-create-group"
                  data-toggle="modal"
                  data-target="#simpleModal"
                >
                  {f({ id: 'carsGroups.addBtn' })}
                </a>
              )}
            </div>
            <div className="col-12 col-md-6 col-lg-3 ml-auto text-right mt-2 mt-md-0">
              <span style={{ textAlign: 'left' }}>
                <input
                  onChange={handleDebouncedAllGroupsSearch}
                  data-cy="input-search-all-groups"
                  type="text"
                  className="form-control border-radius-0"
                  id="input-1"
                  placeholder={f({ id: 'carsGroups.chooseCar' })}
                  title={f({ id: 'carsGroups.chooseCar.tooltip' })}
                />
              </span>
            </div>
          </div>
        </div>

        {isRemoveButtonClicked && (
          <ConfirmDialog
            title={f({ id: 'carsGroups.confirm.dialog.title' })}
            cancelCallback={handleCancelRemove}
            continueCallback={handleContinue}
            cancelButtonTitle={f({ id: 'carsGroups.confirm.dialog.cancel' })}
            continueButtonTitle={f({
              id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.deleteCardConfirmDialog.continue',
            })}
          >
            {f({ id: 'carsGroups.confirm.dialog.message' }, { name: groupNameToRemove })}
          </ConfirmDialog>
        )}

        {Object.keys(groups).length > 0 &&
          Object.values(groups)
            .filter((item: any) => selectedCustomers.includes(item.customerId))
            .map((item: any) => (
              <div className="mt-4" key={`accordion-${item.customerId}-${item.id}`}>
                <CmdAccordion
                  id={item.id}
                  close={!groupsToggle[item.id]}
                  changeCallback={toggleDetail}
                  key={`accordion-${item.id}`}
                  content={
                    <Header
                      item={item}
                      customers={customers}
                      customerIdsInGroup={
                        customerIdsInGroup && String(customerIdsInGroup.groupId) === String(item.id)
                          ? customerIdsInGroup.customerIds
                          : null
                      }
                    />
                  }
                  btnContent={
                    <Actions
                      item={item}
                      handleEditGroupModalOpening={handleEditGroupModalOpening}
                      removeGroup={removeGroup}
                    />
                  }
                >
                  <Body
                    item={item}
                    groupsToggle={groupsToggle}
                    loader={loader}
                    intl={intl}
                    loadCustomerIds={handleCustomerIds}
                    allGroupsString={allGroupsString}
                  />
                </CmdAccordion>
              </div>
            ))}
      </div>
      {isFormOpened && (
        <div>
          <ModalForm
            getFilteredVehiclesGroups={getFilteredVehiclesGroups}
            handleClose={handleClose}
            editingValues={editingValues}
            setGroups={setGroups}
            groups={groups}
            customers={customers}
          />
        </div>
      )}
    </div>
  );
}

export default React.memo(CarsGroup);
